/**
 * Sesión B WF3 — Conversation thread component.
 * Renders a list of {sender_name, sender_role, body, created_at} messages
 * + an input to send a new one. The endpoint is provided by the caller so
 * the same component handles leads, client-creation requests and
 * suite-activation requests.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Send } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_TONE = {
  hm_admin:    'bg-primary/10 text-primary border-primary/30',
  hm_advisor:  'bg-info/10 text-info border-info/30',
  client_admin:'bg-accent/10 text-accent border-accent/30',
  evaluated:   'bg-muted text-muted-foreground border-border',
};

const initials = (name) => (name || '?').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
const fmt = (iso, lang) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(lang?.startsWith('es') ? 'es-ES' : 'en-US', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

// TalentHeart 2026-06 — some request types (program_activation) store thread
// entries with the status-history shape {by_user_name, by_user_role, comment,
// at}. Normalise both shapes so sender, role and timestamp always render.
const normalizeMsg = (m) => ({
  id: m.id,
  sender_name: m.sender_name || m.by_user_name || null,
  sender_role: m.sender_role || m.by_user_role || null,
  body: m.body ?? m.comment ?? '',
  created_at: m.created_at || m.at || null,
});

export default function ConversationThread({ messages = [], onSend, disabled = false, variant = 'default' }) {
  const { t, i18n } = useTranslation();
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const isInternal = variant === 'internal';
  const normalized = messages.map(normalizeMsg);

  const send = async () => {
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    try {
      await onSend(body);
      setDraft('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('pages.requests.thread.sendFailed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={isInternal ? 'internal-conversation-thread' : 'conversation-thread'}>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {isInternal
          ? t('pages.requests.thread.internalTitle', { defaultValue: 'Internal thread (CTH only)' })
          : t('pages.requests.thread.title')}
        <span className="ml-2 text-muted-foreground/60">({normalized.length})</span>
      </p>
      {isInternal && (
        <p className="text-xs rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-2.5 py-1.5" data-testid="internal-thread-note">
          {t('pages.requests.thread.internalNote', {
            defaultValue: 'Visible only to CTH Admin and Advisors — the client can never see this conversation.',
          })}
        </p>
      )}
      <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
        {normalized.length === 0 && (
          <p className="text-xs text-muted-foreground italic" data-testid={isInternal ? 'internal-thread-empty' : 'thread-empty'}>
            {t('pages.requests.thread.empty')}
          </p>
        )}
        {normalized.map((m) => (
          <div key={m.id} className="flex items-start gap-2.5" data-testid={`thread-msg-${m.id}`}>
            <Avatar className="h-8 w-8 shrink-0">
              <AvatarFallback className="text-xs font-bold">{initials(m.sender_name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-sm font-semibold">{m.sender_name || '—'}</span>
                {m.sender_role && (
                  <Badge variant="outline" className={`text-xs ${ROLE_TONE[m.sender_role] || ''}`}>
                    {t(`pages.requests.thread.role.${m.sender_role}`, { defaultValue: m.sender_role })}
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">{fmt(m.created_at, i18n.language)}</span>
              </div>
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</p>
            </div>
          </div>
        ))}
      </div>
      {!disabled && (
        <div className="flex gap-2">
          <Textarea
            rows={2}
            placeholder={isInternal
              ? t('pages.requests.thread.internalPlaceholder', { defaultValue: 'Internal note for the CTH team…' })
              : t('pages.requests.thread.placeholder')}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="flex-1 text-sm"
            data-testid={isInternal ? 'internal-thread-input' : 'thread-input'}
          />
          <Button
            onClick={send}
            disabled={sending || !draft.trim()}
            data-testid={isInternal ? 'internal-thread-send' : 'thread-send'}
            className="self-end"
          >
            <Send className="h-4 w-4 mr-1" />
            {sending ? t('common.sending', { defaultValue: 'Sending…' }) : t('pages.requests.thread.send')}
          </Button>
        </div>
      )}
    </div>
  );
}
